import { useCallback, useEffect, useMemo, useState } from "react";
import { SCATTER_POINTS } from "@/services/mock";
import { useCompareSuggestions } from "@/hooks/useCompareSuggestions";
import {
  CMP_COLORS,
  type CompareFormula,
  type CompareSensory,
  type ComputeCompareStats,
} from "@/utils/types/compare";
import type { Variant } from "@/utils/types/variant";
import type { Target } from "@/utils/types/flavor";
import { buildCompareCatalog } from "@/utils/formulation/buildCompareCatalog";
import { computeCurrent } from "@/utils/formulation/computeCurrent";
import { computeLoss } from "@/utils/formulation/computeLoss";

const MAX_PICKS = 2;

// FNV-1a-style 0..1 hash. Used to deterministically scatter the comparison
// markers near the active flask in the scatter plot.
function hash01(s: string): number {
  let h = 1469598103;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

type UseCompareSelectionArgs = {
  flavorId: string | null;
  variants: Variant[];
  activeVariantId: string;
  target: Target | null | undefined;
};

export type UseCompareSelectionResult = {
  // Pickable formulae, partitioned by source.
  catalog: ReturnType<typeof buildCompareCatalog>;
  // Currently picked alternates (capped at 2; pick order preserved).
  selected: CompareFormula[];
  // Selection mutators — `toggle` no-ops when at cap and the item isn't picked.
  toggle: (f: CompareFormula) => void;
  clear: () => void;
  clearSlot: (id: string) => void;
  // Per-formula loss² + cost — used by the popover row metrics and the
  // ComparePanel column metric rows.
  computeStats: ComputeCompareStats;
  // Sensory overlays consumed by RadarPlot/ScatterPlot. Empty when nothing
  // is picked; entries match `selected` 1:1 with stable colors.
  sensory: CompareSensory[];
  /** True while the server-provided similar + AI suggestions are loading. */
  suggestionsLoading: boolean;
};

// Manages compare selection (cap 2), derived catalog, sensory overlays, and a
// shared computeStats helper. Resets selection whenever the active variant
// changes — mixing the old "Current Flask" with the new one would be confusing.
export function useCompareSelection({
  flavorId,
  variants,
  activeVariantId,
  target,
}: UseCompareSelectionArgs): UseCompareSelectionResult {
  const [selected, setSelected] = useState<CompareFormula[]>([]);
  const suggestionsQ = useCompareSuggestions(flavorId);

  useEffect(() => {
    setSelected([]);
  }, [activeVariantId]);

  const catalog = useMemo(
    () =>
      buildCompareCatalog(
        variants,
        activeVariantId,
        suggestionsQ.data?.similar ?? [],
        suggestionsQ.data?.aiGenerated ?? [],
      ),
    [variants, activeVariantId, suggestionsQ.data],
  );

  const toggle = useCallback((f: CompareFormula) => {
    setSelected((prev) => {
      const has = prev.some((p) => p.id === f.id);
      if (has) return prev.filter((p) => p.id !== f.id);
      if (prev.length >= MAX_PICKS) return prev;
      return [...prev, f];
    });
  }, []);

  const clear = useCallback(() => setSelected([]), []);
  const clearSlot = useCallback(
    (id: string) => setSelected((prev) => prev.filter((p) => p.id !== id)),
    []
  );

  // Similar / AI suggestions ship with `formulation: []` because items are
  // out of scope for the initial plan. Returning null stats lets the popover
  // row fall back to "—" instead of formatting bogus 0.00 / $0.00 values.
  const computeStats = useCallback<ComputeCompareStats>(
    (form) => {
      if (form.length === 0) return { loss: null, cost: null };
      const cur = computeCurrent(form, target);
      return { loss: computeLoss(cur, target), cost: null };
    },
    [target]
  );

  const sensory = useMemo<CompareSensory[]>(() => {
    const baseScatter = SCATTER_POINTS.current;
    return selected.map((sel, i) => {
      const cur = computeCurrent(sel.formulation, target);
      const seed =
        sel.id + "|" + sel.formulation.map((f) => f.compoundId).join(",");
      const dx = (hash01(seed + "x") - 0.5) * 0.1;
      const dy = (hash01(seed + "y") - 0.5) * 0.1;
      return {
        id: sel.id,
        name: sel.name,
        descriptor: sel.descriptor,
        color: CMP_COLORS[i] ?? CMP_COLORS[0],
        taste: cur ? cur.taste : null,
        smell: cur ? cur.smell : null,
        x: Math.max(0.02, Math.min(0.98, baseScatter.x + dx)),
        y: Math.max(0.02, Math.min(0.98, baseScatter.y + dy)),
      };
    });
  }, [selected, target]);

  // Gate on `flavorId !== null` because TanStack Query v5 reports
  // `isPending: true` for disabled queries — without the gate, callers
  // would render "Loading…" forever when there's no flavor selected.
  const suggestionsLoading = flavorId !== null && suggestionsQ.isPending;

  return {
    catalog,
    selected,
    toggle,
    clear,
    clearSlot,
    computeStats,
    sensory,
    suggestionsLoading,
  };
}
